<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;
type ApplicationData = App.Students.Data.ApplicationData;

import type { HybridlyFormData } from '@/@types/global';

import { getById as getInstituteById } from '@/api/InstituteApi';

import Divider from '@/components/divider/Divider.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import FormCheckboxInput from '@/components/formCheckboxInput/FormCheckboxInput.vue';
import FormDateInput from '@/components/formDateInput/FormDateInput.vue';
import FormInlineFields from '@/components/formInlineFields/FormInlineFields.vue';
import FormInstituteInput from '@/components/formComboboxInput/FormInstituteInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

const props = defineProps<{
  form: HybridlyFormData<{
    institute_id: number;
    date_submitted: string;
    status: EnumData;
    intend_to_enroll: EnumData;
    has_waiver: EnumData;
    waiver: number;
    first_choice: boolean;
    award_meeting_held: boolean;
    early_action: boolean;
    early_decision: boolean;
    sent_hs_transcript: boolean;
    applied_to_honors_program: boolean;
    financial_aid_submitted_to_school: boolean;
  }>;
  application?: ApplicationData;
  dataRequestType?: string;
  isReadonly?: boolean;
  statuses: EnumData[];
}>();

function handleStatusChange(status) {
  props.form.fields.status = status;
  props.form.fields.intend_to_enroll = false;
}

function handleInstituteChange(institute) {
  props.form.fields.institute_id = institute;

  if (props.form.fields.has_waiver?.value) {
    updateWaiverAmount(institute?.value);
  }
}

function updateWaiverAmount(instituteId) {
  if (!instituteId) {
    props.form.fields.waiver = null;
    return;
  }

  getInstituteById(instituteId)
    .then((institute) => {
      props.form.fields.waiver = institute.waiver_fee ?? null;
    })
    .catch(() => {
      props.form.fields.waiver = null;
    });
}

function handleHasWaiverChange(hasWaiver) {
  props.form.fields.has_waiver = hasWaiver;
  props.form.fields.waiver = null;

  if (hasWaiver.value) {
    updateWaiverAmount(props.form.fields.institute_id?.value);
  } else {
    props.form.fields.waiver = null;
  }
}

onMounted(() => {
  if (props.form.fields.has_waiver?.value) {
    updateWaiverAmount(props.form.fields.institute_id?.value);
  }
});
</script>

<template>
  <Fieldset title="Global information">
    <FormInstituteInput
      :error="form.errors.institute_id"
      :isDisabled="isReadonly"
      :modelValue="form.fields.institute_id"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.institute_id?.label, application?.institute?.name)
          : undefined
      "
      label="Institution"
      @update:modelValue="handleInstituteChange"
    />
    <FormDateInput
      :isDisabled="true"
      :error="form.errors.date_submitted"
      :modelValue="form.fields.date_submitted"
      label="Application Status Date"
      helperText="The application status date is automatically set when the college application has an in progress or submitted status."
      name="date_submitted"
      format="MMM dd, yyyy"
      :clearable="false"
      @update:modelValue="(value) => (form.fields.date_submitted = value)"
    />
    <FormListboxInput
      :error="form.errors.status"
      :isDisabled="isReadonly"
      :modelValue="form.fields.status"
      :options="statuses"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.status?.label, application?.status?.label)
          : undefined
      "
      label="Status"
      @update:modelValue="handleStatusChange"
    />
    <FormListboxInput
      :error="form.errors.intend_to_enroll"
      :isDisabled="form.fields.status?.value !== 'accepted' || isReadonly"
      :modelValue="form.fields.intend_to_enroll"
      :options="[
        {
          label: 'No',
          value: false
        },
        {
          label: 'Yes',
          value: true
        }
      ]"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.intend_to_enroll?.value, application?.intend_to_enroll)
          : undefined
      "
      label="Intend to Enroll"
      helperText="You can set this field when the status is Accepted only"
      @update:modelValue="(value) => (form.fields.intend_to_enroll = value)"
    />
  </Fieldset>
  <Divider />
  <Fieldset title="Waiver">
    <FormInlineFields>
      <FormListboxInput
        :error="form.errors.has_waiver"
        :isDisabled="isReadonly"
        :modelValue="form.fields.has_waiver"
        :options="[
          {
            label: 'No',
            value: false
          },
          {
            label: 'Yes',
            value: true
          }
        ]"
        :previousValue="
          !isReadonly
            ? previousValueLabel(
                form.fields.has_waiver?.label,
                application ? (application?.has_waiver ? 'Yes' : 'No') : null
              )
            : undefined
        "
        label="Has Waiver"
        @update:modelValue="handleHasWaiverChange"
      />
      <FormTextInput
        :error="form.errors.waiver"
        :isDisabled="!form.fields.has_waiver?.value || isReadonly"
        :modelValue="form.fields.waiver"
        :previousValue="
          !isReadonly ? previousValueLabel(form.fields.waiver, application?.waiver) : undefined
        "
        label="Waiver Amount"
        name="waiver"
        placeholder="Enter amount"
        prepend="$"
        type="number"
        @update:modelValue="(value) => (form.fields.waiver = value)"
      />
    </FormInlineFields>
  </Fieldset>
  <Divider />
  <Fieldset title="Application information">
    <FormCheckboxInput
      :checked="form.fields.first_choice"
      :error="form.errors.first_choice"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.first_choice, application?.first_choice)
          : undefined
      "
      label="First Choice"
      name="first_choice"
      @update:checked="(value) => (form.fields.first_choice = value)"
    />

    <FormCheckboxInput
      :checked="form.fields.early_action"
      :error="form.errors.early_action"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.early_action, application?.early_action)
          : undefined
      "
      label="Early Action"
      name="early_action"
      @update:checked="(value) => (form.fields.early_action = value)"
    />
    <FormCheckboxInput
      :checked="form.fields.early_decision"
      :error="form.errors.early_decision"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.early_decision, application?.early_decision)
          : undefined
      "
      label="Early Decision"
      name="early_decision"
      @update:checked="(value) => (form.fields.early_decision = value)"
    />
    <FormCheckboxInput
      :checked="form.fields.sent_hs_transcript"
      :error="form.errors.sent_hs_transcript"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.sent_hs_transcript, application?.sent_hs_transcript)
          : undefined
      "
      label="Sent HS Transcript"
      name="sent_hs_transcript"
      @update:checked="(value) => (form.fields.sent_hs_transcript = value)"
    />
    <FormCheckboxInput
      :checked="form.fields.applied_to_honors_program"
      :error="form.errors.applied_to_honors_program"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(
              form.fields.applied_to_honors_program,
              application?.applied_to_honors_program
            )
          : undefined
      "
      label="Applied to Honors Program"
      name="applied_to_honors_program"
      @update:checked="(value) => (form.fields.applied_to_honors_program = value)"
    />
    <FormCheckboxInput
      :checked="form.fields.financial_aid_submitted_to_school"
      :error="form.errors.financial_aid_submitted_to_school"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(
              form.fields.financial_aid_submitted_to_school,
              application?.financial_aid_submitted_to_school
            )
          : undefined
      "
      label="Financial Aid Submitted to School"
      name="financial_aid_submitted_to_school"
      @update:checked="(value) => (form.fields.financial_aid_submitted_to_school = value)"
    />
    <FormCheckboxInput
      :isDisabled="true"
      :checked="form.fields.award_meeting_held"
      :error="form.errors.award_meeting_held"
      label="1:1 Award Letter Meeting Held"
      name="award_meeting_held"
      helperText="This field is automatically set via the student's interactions."
      @update:checked="(value) => (form.fields.award_meeting_held = value)"
    />
  </Fieldset>
</template>
